import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { searchableSelectAutocompleteProps } from '../SearchableSelect';
import tesoreriaService, { type GrupoCajaChica } from '../../services/tesoreriaService';
import { findGrupoCajaGuardado, persistGrupoCajaChica } from './grupoCajaStorage';

export interface GrupoCajaSelectProps {
    usuario: string;
    value: GrupoCajaChica | null;
    onChange: (grupo: GrupoCajaChica | null) => void;
    locked?: boolean;
    label?: string;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    size?: 'small' | 'medium';
}

const GrupoCajaSelect: React.FC<GrupoCajaSelectProps> = ({
    usuario,
    value,
    onChange,
    locked = false,
    label = 'Grupo de caja',
    required = false,
    error = false,
    helperText,
    size = 'small',
}) => {
    const [grupos, setGrupos] = useState<GrupoCajaChica[]>([]);
    const [loading, setLoading] = useState(false);
    const restoredRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        restoredRef.current = false;

        const cargarGrupos = async () => {
            setLoading(true);
            try {
                const list = await tesoreriaService.getGruposCajaChica({ usuario });
                if (cancelled) return;
                const gruposList = list || [];
                setGrupos(gruposList);

                if (!restoredRef.current && !value && gruposList.length > 0) {
                    const restored = findGrupoCajaGuardado(gruposList);
                    if (restored) {
                        restoredRef.current = true;
                        onChange(restored);
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Error al cargar grupos de caja:', err);
                    setGrupos([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        cargarGrupos();
        return () => {
            cancelled = true;
        };
    }, [usuario]);

    const handleChange = (_: unknown, newValue: GrupoCajaChica | null) => {
        if (locked) return;
        onChange(newValue);
        if (newValue) {
            persistGrupoCajaChica(newValue);
        }
    };

    const lockedHelper = locked ? 'No se puede cambiar con medios de pago cargados' : undefined;

    return (
        <Autocomplete
            {...searchableSelectAutocompleteProps}
            options={grupos}
            getOptionLabel={(option) => option.nombre || `Grupo ${option.codigo}`}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={value}
            onChange={handleChange}
            disabled={locked}
            loading={loading}
            renderInput={(params) => (
                <TextField
                    {...params}
                    size={size}
                    label={label}
                    placeholder="Seleccionar grupo..."
                    required={required}
                    error={error}
                    helperText={helperText ?? lockedHelper}
                />
            )}
        />
    );
};

export default GrupoCajaSelect;
